import { i18n, MessageDescriptor } from '@lingui/core';
import { defineMessage } from '@lingui/core/macro';
import { CustomPartCategoryDTO, CustomPartCategoryOriginEnum } from '@luminovo/api-contracts';

/**
 * The categories every tenant starts with, keyed by the English name the seed migration
 * (`2025-11-06-184545_add_custom_part_categories`) stores them under. Like every other category they are plain
 * strings in the database, so the UI translates them by name.
 */
const defaultCustomPartCategoryTranslations: Partial<Record<string, MessageDescriptor>> = {
    PCB: defineMessage({ message: 'PCB' }),
    Mechanical: defineMessage({ message: 'Mechanical' }),
    Plastic: defineMessage({ message: 'Plastic' }),
    Cable: defineMessage({
        message: 'Cable',
        context: 'custom part category',
        comment: 'Cable as custom part specification category',
    }),
    Packaging: defineMessage({
        message: 'Packaging',
        context: 'custom part category',
        comment: 'Packaging as custom part specification category, not the packaging form of an offer',
    }),
    Label: defineMessage({
        message: 'Label',
        context: 'custom part category',
        comment: 'Label as custom part specification category',
    }),
    Other: defineMessage({ message: 'Other' }),
};

/**
 * The name to show for a custom part category: a default category in the user's language, a category a user or
 * an import created exactly as it was entered. A default category the API renamed keeps its stored name.
 */
export function formatCustomPartCategoryName(category: Pick<CustomPartCategoryDTO, 'category' | 'origin'>): string {
    if (category.origin !== CustomPartCategoryOriginEnum.System) {
        return category.category;
    }
    const message = defaultCustomPartCategoryTranslations[category.category];
    return message ? i18n._(message) : category.category;
}
